import { cn } from "@/lib/utils";

/**
 * Consistent page container widths used across all pages.
 *
 * WIDE     → max-w-6xl  (1152px) — data-heavy: admin tables, settings with sidebar, dashboards
 * NORMAL   → max-w-5xl  (1024px) — standard content: home, forums, messages, profiles
 * NARROW   → max-w-md   (448px)  — focused forms: auth, reset password, join adventure
 */

type ContainerWidth = "wide" | "normal" | "narrow";

const WIDTH_CLASSES: Record<ContainerWidth, string> = {
  wide: "max-w-6xl",
  normal: "max-w-5xl",
  narrow: "max-w-md",
};

interface PageContainerProps {
  width?: ContainerWidth;
  /** Center content vertically (for narrow auth-style pages) */
  center?: boolean;
  /** Remove default vertical padding */
  noPadding?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Wraps page content in a consistent, responsive container.
 *
 * Usage:
 *   <PageContainer width="normal">…</PageContainer>
 *   <PageContainer width="narrow" center>…</PageContainer>
 */
export function PageContainer({
  width = "normal",
  center = false,
  noPadding = false,
  className,
  children,
}: PageContainerProps) {
  return (
    <div
      className={cn(
        "min-h-[calc(100vh-3.5rem)] bg-background",
        center && "flex items-center justify-center",
        className,
      )}
    >
      <div
        className={cn(
          WIDTH_CLASSES[width],
          "mx-auto w-full px-4 sm:px-6",
          !noPadding && !center && "py-6 sm:py-8",
          center && "py-6",
        )}
      >
        {children}
      </div>
    </div>
  );
}
